import {
  LoginContextType,
  LoginContextUserType,
  LoginContextVerificationDataType,
  UserAdditionalDataInput
} from '../LoginContext'
import { UserSecurityQuestionsEnum } from 'shared/enums/UserSecurityQuestionsEnum'
import { action, observable } from 'mobx'
import { LoginPageQuery } from '../LoginPage.codegen'
import { SecurityQuestionsDataInput } from '../../../../graphql/public/PublicSchemaTypes'
import { pick } from 'lodash'
import React from 'react'
import { LoginEmailStep } from '../../email/LoginEmailStep'
import { SignupWithSecretLinkUserDataFormStep } from '../../signup-with-secret-link/SignupWithSecretLinkUserDataFormStep'
import { AllowedUserFieldsEnum } from 'shared/accountNameConfig'

export type AuthStateConstructorArgs = {
  selfSignupToken?: string | null
  organisation: NonNullable<NonNullable<LoginPageQuery>['organisation']>
}

export class AuthState implements LoginContextType {
  @observable isAccountNameLoginOn: boolean = false
  @observable userData: LoginContextUserType = null
  @observable isExistingUser: boolean = false
  @observable userIdentity: string = ''
  @observable selfSignupToken?: string | null
  @observable usedSecurityQuestions: UserSecurityQuestionsEnum[] | null = null
  @observable userHasEmail: boolean = false
  @observable password: string = ''
  @observable verificationData: LoginContextVerificationDataType = null
  @observable organisation: NonNullable<
    NonNullable<LoginPageQuery>['organisation']
  >
  @observable additionalData: UserAdditionalDataInput
  @observable securityQuestions: SecurityQuestionsDataInput[]
  @observable stepView: React.FC

  constructor({ selfSignupToken, organisation }: AuthStateConstructorArgs) {
    this.selfSignupToken = selfSignupToken
    this.organisation = organisation
    this.isAccountNameLoginOn =
      organisation?.features?.account_name_login ?? false
    this.stepView = selfSignupToken
      ? SignupWithSecretLinkUserDataFormStep
      : LoginEmailStep
  }

  @action
  setStepView = (stepView: React.FC) => {
    this.stepView = stepView
  }

  @action
  setAdditionalData = (data: UserAdditionalDataInput) => {
    this.additionalData = data
  }

  @action
  setSecurityQuestions = (data: SecurityQuestionsDataInput[]) => {
    this.securityQuestions = data
  }

  @action
  setPassword = (password: string) => {
    this.password = password
  }

  @action
  setUserIdentity = (userIdentity: string) => {
    this.userIdentity = userIdentity
  }

  @action
  setVerificationData = (data: LoginContextVerificationDataType) => {
    this.verificationData = data
  }

  @action
  setIsExistingUser = (exists: boolean) => {
    this.isExistingUser = exists
  }

  @action
  setUsedSecurityQuestions = (sq: UserSecurityQuestionsEnum[] | null) => {
    this.usedSecurityQuestions = sq
  }

  @action
  setUserHasEmail = (has: boolean) => {
    this.userHasEmail = has
  }

  @action
  setUserData = (
    userData: Partial<LoginContextUserType>,
    replace?: boolean
  ) => {
    if (replace) {
      this.userData = { ...userData } as LoginContextUserType
    } else {
      this.userData = {
        ...this.userData,
        ...userData
      } as LoginContextUserType
    }
  }

  get additionalFieldsList(): AllowedUserFieldsEnum[] {
    return (
      (this.organisation?.additionalFieldsConfig
        ?.additionalFields as unknown as AllowedUserFieldsEnum[]) ?? []
    )
  }

  shouldAskSecurityQuestions = (): boolean => {
    return (
      !this.userHasEmail &&
      this.isAccountNameLoginOn &&
      !this.usedSecurityQuestions?.length &&
      !this.additionalData?.email
    )
  }

  pickUserAdditionalData(): UserAdditionalDataInput {
    if (!this.additionalFieldsList) {
      return {}
    }

    return pick({ ...this.userData, ...this.additionalData }, [
      ...this.additionalFieldsList
    ])
  }

  get privacyPolicyEnabled() {
    return !!this.organisation?.additionalFieldsConfig?.privacyPolicyEnabled
  }

  get privacyPolicyUrl() {
    return (
      (this.privacyPolicyEnabled &&
        this.organisation?.additionalFieldsConfig?.privacyPolicyUrl) ||
      null
    )
  }

  get userNeedToAcceptPrivacyPolicy() {
    return !!(
      this.privacyPolicyEnabled &&
      this.privacyPolicyUrl &&
      !this.userData?.privacy_policy_accepted
    )
  }
}
